<script setup lang="ts">
import { AlertTriangle, Bell, CheckCircle2, ExternalLink } from '@lucide/vue'
import type { PublicNotificationDto } from '~~/shared/types/notification'

const props = defineProps<{ notification: PublicNotificationDto; compact?: boolean }>()
const emit = defineEmits<{ open: [notification: PublicNotificationDto] }>()
const categoryLabel = computed(
  () =>
    ({
      incident: 'Incident',
      issue: 'Problème Morning',
      announcement: 'Annonce',
      maintenance: 'Maintenance',
      account: 'Compte',
      security: 'Sécurité',
      system: 'Système',
    })[
      props.notification.category as
        'incident' | 'issue' | 'announcement' | 'maintenance' | 'account' | 'security' | 'system'
    ] ?? props.notification.category,
)
const relative = computed(() => {
  const seconds = Math.round((new Date(props.notification.createdAt).getTime() - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second')
  const minutes = Math.round(seconds / 60)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  return formatter.format(Math.round(hours / 24), 'day')
})
</script>

<template>
  <button
    class="notification-item analytics-sensitive"
    :class="[`severity-${notification.severity}`, { unread: !notification.readAt, compact }]"
    type="button"
    @click="emit('open', notification)"
  >
    <span class="notification-item-icon"
      ><AlertTriangle
        v-if="notification.severity === 'critical' || notification.severity === 'warning'"
        :size="17" /><CheckCircle2
        v-else-if="notification.severity === 'success'"
        :size="17" /><Bell
        v-else
        :size="17" /></span
    ><span class="notification-item-copy"
      ><span
        v-if="!compact"
        class="notification-item-context"
        ><span>{{ categoryLabel }}</span
        ><span>{{ notification.eventType }}</span
        ><span v-if="notification.mandatory">Obligatoire</span></span
      ><span
        ><strong>{{ notification.title }}</strong
        ><i
          v-if="!notification.readAt"
          aria-label="Non lue" /></span
      ><small>{{ notification.body }}</small
      ><time
        :datetime="notification.createdAt"
        :title="new Date(notification.createdAt).toLocaleString('fr-FR')"
        >{{ relative }}</time
      ></span
    ><ExternalLink
      v-if="notification.actionUrl"
      :size="14"
      aria-hidden="true"
    />
  </button>
</template>
